import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  ContinueWithSettingsUi,
  FrontendApi,
  RecoveryFlow,
  RecoveryFlowState,
  RegistrationFlow,
} from '@ory/client';
import { from, of as observableOf } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import * as actions from './recovery.actions';
import { auth as authSelectors } from '../auth.selectors';
import { State } from '../../reducers';

export interface KratosError {
  error: {
    id: string;
    code: number;
    status: string;
    reason: string;
    message: string;
  };
  redirect_browser_to?: string;
}

@Injectable()
export class AuthRecoveryEffects {
  beginRecovery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestBeginAccountRecovery),
      switchMap((action) =>
        from(
          this.kratos.createBrowserRecoveryFlow().then((flow) =>
            this.kratos.updateRecoveryFlow({
              flow: flow.data.id,
              updateRecoveryFlowBody: {
                method: 'code',
                email: action.email,
                csrf_token: flow.data.ui.nodes.find((n) => n.attributes['name'] === 'csrf_token')
                  .attributes['value'],
              },
            })
          )
        ).pipe(
          map((res) => actions.SuccessBeginAccountRecovery({ flow: res.data })),
          catchError((err) => observableOf(actions.FailBeginAccountRecovery({ error: err })))
        )
      )
    )
  );

  completeRecovery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestCompleteAccountRecovery),
      withLatestFrom(this.store.select(authSelectors.getRecoveryFlow)),
      switchMap(([action, flow]) =>
        from(
          this.kratos
            .updateRecoveryFlow({
              flow: flow.id,
              updateRecoveryFlowBody: {
                code: action.code,
                method: 'code',
                csrf_token: flow.ui.nodes.find((n) => n.attributes['name'] === 'csrf_token')
                  .attributes['value'],
              },
            })
            .then((res) => res.data)
            // we need the error payloads, and they get stripped away if we just catch the
            // observable error
            .catch((err) => {
              return err.response.data;
            })
        ).pipe(
          map((res: RecoveryFlow | KratosError) => {
            // Kratos 1.1.0 introduced continue with flows as a way of navigating
            // from recovery flow to settings flow. This is enabled via feature
            // flag which **should** be enabled now.
            if ((<RecoveryFlow>res)?.continue_with?.length > 0) {
              const settingsTask = (<RecoveryFlow>res)?.continue_with.find(
                (task) => task.action === 'show_settings_ui'
              );
              return actions.SuccessCompleteAccountRecovery({
                res: res,
                passwordChangeFlowId: (<ContinueWithSettingsUi>settingsTask).flow.id,
              });
              // Pre- Kratos 1.1.0 used an error to indicate that the browser needed to navigate
              // to a settings flow. I'm not sure if this is ever still the case, which is
              // why this code is still here.
            } else if ((<KratosError>res)?.error?.id === 'browser_location_change_required') {
              const passwordChangeFlowId = (<KratosError>res).redirect_browser_to?.match(
                /\?flow=([a-z0-9\-]+)/
              )[1];
              return actions.SuccessCompleteAccountRecovery({ res: res, passwordChangeFlowId });
            } else if ((<RecoveryFlow>res).state === RecoveryFlowState.SentEmail) {
              const message = (<RecoveryFlow>res).ui.messages[0].text;
              return actions.FailCompleteAccountRecovery({ error: message });
            }
            return actions.FailCompleteAccountRecovery({ error: res });
          }),
          catchError((res: KratosError) => {
            if (res.error?.id === 'browser_location_change_required') {
              const passwordChangeFlowId =
                res.redirect_browser_to?.match(/\?flow=([a-z0-9\-]+)/)[1];
              return observableOf(
                actions.SuccessCompleteAccountRecovery({ res: res, passwordChangeFlowId })
              );
            }
            return observableOf(actions.FailCompleteAccountRecovery({ error: res }));
          })
        )
      )
    )
  );

  constructor(
    private store: Store<State>,
    private actions$: Actions,
    private kratos: FrontendApi
  ) {}
}
